export const isTestEnvironment = Boolean(
  process.env.PLAYWRIGHT_TEST_BASE_URL ||
  process.env.PLAYWRIGHT ||
  process.env.CI_PLAYWRIGHT
);

/**
 * Helper functions for reCAPTCHA action ID generation.
 * Inlined here to avoid importing from lib/utils which has Node.js dependencies
 * that are incompatible with Edge Runtime middleware.
 */
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

function alphaEncode(value: string, length = 32): string {
  // encode a string into a standard set of alpha chars of a given length
  // e.g. "this is a test string" -> "MMPHLRqmbjLbOamGCatJohkFwlMtQVEp"
  let out = "";
  let acc = 0;

  for (let i = 0; i < value.length; i += 1) {
    acc = (acc + value.charCodeAt(i) * (i + 1)) % ALPHABET.length;
    out += ALPHABET[acc];
  }

  // Stretch if input is short
  while (out.length < length) {
    acc = (acc * 7 + 13) % ALPHABET.length;
    out += ALPHABET[acc];
  }

  return out.slice(0, length);
}

function createRecaptchaActionId(verticalName: string, action: string): string {
  return `${alphaEncode(verticalName)}_${action}`;
}

export const getFullyQualifiedDomain = (): string => {
  const FULLY_QUALIFIED_DOMAIN = process.env.FULLY_QUALIFIED_DOMAIN;
  if (!FULLY_QUALIFIED_DOMAIN)
    throw new Error("FULLY_QUALIFIED_DOMAIN is not set in environment variables");

  return FULLY_QUALIFIED_DOMAIN;
}

export const getFullyQualifiedBaseUrl = () => {
  const FULLY_QUALIFIED_DOMAIN = getFullyQualifiedDomain();
  return FULLY_QUALIFIED_DOMAIN.includes("localhost:")
    ? `http://${FULLY_QUALIFIED_DOMAIN}`
    : `https://${FULLY_QUALIFIED_DOMAIN}`;
}

export const getFullyQualifiedA2ADomain = (): string => {
  const FULLY_QUALIFIED_A2A_DOMAIN = process.env.FULLY_QUALIFIED_A2A_DOMAIN || process.env.FULLY_QUALIFIED_DOMAIN;
  if (!FULLY_QUALIFIED_A2A_DOMAIN)
    throw new Error("Could not determine A2A base domain from environment variables. One of FULLY_QUALIFIED_A2A_DOMAIN or FULLY_QUALIFIED_DOMAIN must be set.");

  return FULLY_QUALIFIED_A2A_DOMAIN;
}

export const getRapidsProjectId = (): string => {
  const RAPIDS_PROJECT_ID = process.env.RAPIDS_PROJECT_ID;
  if (!RAPIDS_PROJECT_ID)
    throw new Error("RAPIDS_PROJECT_ID is not set in environment variables");

  return RAPIDS_PROJECT_ID;
}

export const getRapidsVerticalName = () => {
  const RAPIDS_VERTICAL_NAME = process.env.RAPIDS_VERTICAL_NAME;
  if (!RAPIDS_VERTICAL_NAME)
    throw new Error("RAPIDS_VERTICAL_NAME is not set in environment variables");

  return RAPIDS_VERTICAL_NAME;
};

export const getEnvironment = (): string => {
  const ENVIRONMENT = process.env.ENVIRONMENT;
  if (!ENVIRONMENT)
    throw new Error("ENVIRONMENT is not set in environment variables");

  return ENVIRONMENT;
};

export const getGcpLocation = (): string => {
  const LOCATION = process.env.LOCATION;
  if (!LOCATION)
    throw new Error("LOCATION is not set in environment variables");

  return LOCATION;
}

export const getAuth0Domain = (): string => {
  const AUTH0_DOMAIN = process.env.AUTH0_DOMAIN;
  if (!AUTH0_DOMAIN)
    throw new Error("AUTH0_DOMAIN is not set in environment variables");

  return AUTH0_DOMAIN;
}

export const getAuth0ClientId = (): string => {
  const AUTH0_CLIENT_ID = process.env.AUTH0_CLIENT_ID;
  if (!AUTH0_CLIENT_ID)
    throw new Error("AUTH0_CLIENT_ID is not set in environment variables");

  return AUTH0_CLIENT_ID;
}

export const getAuth0ClientSecret = (): string => {
  const AUTH0_CLIENT_SECRET = process.env.AUTH0_CLIENT_SECRET;
  if (!AUTH0_CLIENT_SECRET)
    throw new Error("AUTH0_CLIENT_SECRET is not set in environment variables");

  return AUTH0_CLIENT_SECRET;
}

export const getAuth0Secret = (): string => {
  const AUTH0_SECRET = process.env.AUTH0_SECRET;
  if (!AUTH0_SECRET)
    throw new Error("AUTH0_SECRET is not set in environment variables");

  return AUTH0_SECRET;
}

export const getAuth0ManagementDomain = (): string => {
  const AUTH0_MANAGEMENT_DOMAIN = process.env.AUTH0_MANAGEMENT_DOMAIN;
  if (!AUTH0_MANAGEMENT_DOMAIN)
    throw new Error("AUTH0_MANAGEMENT_DOMAIN is not set in environment variables");

  return AUTH0_MANAGEMENT_DOMAIN;
}

export const getAuth0OrgId = (): string => {
  const AUTH0_ORG_ID = process.env.AUTH0_ORG_ID;
  if (!AUTH0_ORG_ID)
    throw new Error("AUTH0_ORG_ID is not set in environment variables");

  return AUTH0_ORG_ID;
}

export const getAuth0RapidsDbConnectionId = (): string => {
  const AUTH0_RAPIDS_DB_CONNECTION_ID = process.env.AUTH0_RAPIDS_DB_CONNECTION_ID;
  if (!AUTH0_RAPIDS_DB_CONNECTION_ID)
    throw new Error("AUTH0_RAPIDS_DB_CONNECTION_ID is not set in environment variables");

  return AUTH0_RAPIDS_DB_CONNECTION_ID;
}

export const getZohoGenerateSignupApiKey = (): string => {
  const ZOHO_GENERATE_SIGNUP_API_KEY = process.env.ZOHO_GENERATE_SIGNUP_API_KEY;
  if (!ZOHO_GENERATE_SIGNUP_API_KEY)
    throw new Error("ZOHO_GENERATE_SIGNUP_API_KEY is not set in environment variables");

  return ZOHO_GENERATE_SIGNUP_API_KEY;
}


export const getZohoClientId = (): string => {
  const ZOHO_CLIENT_ID = process.env.ZOHO_CLIENT_ID;
  if (!ZOHO_CLIENT_ID)
    throw new Error("ZOHO_CLIENT_ID is not set in environment variables");

  return ZOHO_CLIENT_ID;
}

export const getZohoClientSecret = (): string => {
  const ZOHO_CLIENT_SECRET = process.env.ZOHO_CLIENT_SECRET;
  if (!ZOHO_CLIENT_SECRET)
    throw new Error("ZOHO_CLIENT_SECRET is not set in environment variables");

  return ZOHO_CLIENT_SECRET;
}

export const getZohoRefreshToken = (): string => {
  const ZOHO_REFRESH_TOKEN = process.env.ZOHO_REFRESH_TOKEN;
  if (!ZOHO_REFRESH_TOKEN)
    throw new Error("ZOHO_REFRESH_TOKEN is not set in environment variables");

  return ZOHO_REFRESH_TOKEN;
}


export const getZohoDeskApiUrl = (): string => {
  const ZOHO_DESK_API_URL = process.env.ZOHO_DESK_API_URL;
  if (!ZOHO_DESK_API_URL)
    throw new Error("ZOHO_DESK_API_URL is not set in environment variables");

  return ZOHO_DESK_API_URL;
}

export const getZohoDeskDepartmentId = (): string => {
  const ZOHO_DESK_DEPARTMENT_ID = process.env.ZOHO_DESK_DEPARTMENT_ID;
  if (!ZOHO_DESK_DEPARTMENT_ID)
    throw new Error("ZOHO_DESK_DEPARTMENT_ID is not set in environment variables");

  return ZOHO_DESK_DEPARTMENT_ID;
}

export const getZohoDeskOrgId = (): string => {
  const ZOHO_DESK_ORG_ID = process.env.ZOHO_DESK_ORG_ID;
  if (!ZOHO_DESK_ORG_ID)
    throw new Error("ZOHO_DESK_ORG_ID is not set in environment variables");

  return ZOHO_DESK_ORG_ID;
}

export const getZohoDeskRefreshToken = (): string => {
  const ZOHO_DESK_REFRESH_TOKEN = process.env.ZOHO_DESK_REFRESH_TOKEN;
  if (!ZOHO_DESK_REFRESH_TOKEN)
    throw new Error("ZOHO_DESK_REFRESH_TOKEN is not set in environment variables");

  return ZOHO_DESK_REFRESH_TOKEN;
}

export const getRecaptchaSiteId = (): string => {
  const RECAPTCHA_SITE_ID = process.env.RECAPTCHA_SITE_ID;
  if (!RECAPTCHA_SITE_ID)
    throw new Error("RECAPTCHA_SITE_ID is not set in environment variables");

  return RECAPTCHA_SITE_ID;
}

export const getRecaptchaSupportTicketActionId = (): string => {
  const RAPIDS_VERTICAL_NAME = getRapidsVerticalName();

  // create constant recaptcha action name that is shared between the frontend and backend.
  return createRecaptchaActionId(RAPIDS_VERTICAL_NAME, 'submit_support_ticket');
};

export const getMaintenanceMode = (): boolean => {
  const MAINTENANCE_MODE = process.env.MAINTENANCE_MODE;
  return MAINTENANCE_MODE?.trim().toLowerCase() === 'true';
};
